import { matchesObservationAuthor, matchesObservationOwner, type ObservationEditProfile } from '@/services/observationTeam';
import { getLocalObservationPrivate, getLocalObservationPrivateOwner } from '@/utils/observationPrivacy';
import type { Observation } from '@/types';

function refsLooseMatch(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.length >= 6 && y.length >= 6 && (x.includes(y) || y.includes(x))) return true;
  return false;
}

/**
 * Observation publique, ou privée visible uniquement par son auteur.
 * Secours : flag local (créé sur cet appareil) si le `tifosi` API est absent.
 */
export function isObservationVisibleToViewer(
  observation: Observation,
  viewerTifosiCle?: string | null,
  profile?: ObservationEditProfile | null,
): boolean {
  if (!observation.isPrivate) return true;

  const cle = observation.cle || observation.id;
  // Flag local = cette session/appareil a enregistré l’obs en privé → toujours visible ici.
  if (getLocalObservationPrivate(cle)) return true;

  if (matchesObservationOwner(observation, viewerTifosiCle ?? undefined)) return true;

  const localOwner = getLocalObservationPrivateOwner(cle);
  const viewer = (viewerTifosiCle || '').trim();
  if (localOwner && viewer && refsLooseMatch(localOwner, viewer)) return true;

  if (profile && matchesObservationAuthor(observation, profile)) return true;

  return false;
}

/** Prénom + nom connus (au moins l’un des deux), avec repli sur `playerName`. */
export function getObservationPlayerName(observation: Observation): string {
  let prenom = (observation.prenom || '').trim();
  let nom = (observation.nom || '').trim();

  if (!prenom && !nom) {
    const fromPlayerName = (observation.playerName || '').trim();
    if (fromPlayerName && fromPlayerName !== 'Joueur') {
      const parts = fromPlayerName.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        prenom = parts[0];
        nom = parts.slice(1).join(' ');
      } else if (parts.length === 1) {
        prenom = parts[0];
      }
    }
  }

  return [prenom, nom].filter(Boolean).join(' ');
}

export function hasObservationPlayerName(observation: Observation): boolean {
  return getObservationPlayerName(observation).length > 0;
}

/** Titre de carte : nom du joueur si connu, sinon N° + club. */
export function formatObservationPlayerLabel(observation: Observation): string {
  const club = (observation.club || observation.teamName || '').trim();
  const namePart = getObservationPlayerName(observation);

  if (namePart) {
    return club ? `${namePart} de ${club}` : namePart;
  }

  const number = (observation.playerNumber || '').replace(/[^\d#]/g, '').replace(/^#/, '');
  if (number && club) return `N°${number} de ${club}`;
  if (number) return `N°${number}`;
  if (club) return `Joueur de ${club}`;
  return observation.playerName?.trim() || 'Joueur';
}

/** Libellé court du poste pour badge liste (GAR, DEF, MIL, ATT). */
export function formatObservationPosteBadge(poste?: string | null): string | null {
  const raw = String(poste ?? '').trim().toUpperCase();
  if (!raw) return null;
  const labels: Record<string, string> = {
    GARDIEN: 'GAR',
    DEFENSEUR: 'DEF',
    MILIEU: 'MIL',
    ATTAQUANT: 'ATT',
  };
  return labels[raw] ?? raw;
}

export function getObservationPlayerInitials(observation: Observation): string {
  const prenom = (observation.prenom || '').trim();
  const nom = (observation.nom || '').trim();
  if (prenom && nom) {
    return `${prenom[0]}${nom[0]}`.toUpperCase();
  }
  const name = getObservationPlayerName(observation);
  if (name.length >= 2) return name.slice(0, 2).toUpperCase();
  if (name.length === 1) return name[0].toUpperCase();
  return '?';
}
